import { Popover, Transition } from '@headlessui/react';
import { bem } from '@socialive/common';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { useHover } from '../../../hooks';
import Portal from '../Portal';
import './tooltip.css';

/*
#tailwind
*/

function Tooltip({
  disabled,
  children,
  visible,
  content,
  placement,
  strategy,
  className,
  tooltipAnchorClassName,
  xOffset,
  yOffset,
  showArrow,
  ...other
}) {
  const [ref, setRef] = useState();
  const [popperElement, setPopperElement] = useState();
  const [hover, hoverProps] = useHover(ref);
  const [arrowElement, setArrowElement] = useState(null);

  const { styles, attributes } = usePopper(ref, popperElement, {
    placement,
    strategy,
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: [
            'auto',
            'auto-start',
            'auto-end',
            'top',
            'top-start',
            'top-end',
            'bottom',
            'bottom-start',
            'bottom-end',
            'right',
            'right-start',
            'right-end',
            'left',
            'left-start',
            'left-end',
          ],
        },
      },
      {
        name: 'offset',
        options: {
          offset: [xOffset, yOffset],
        },
      },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });

  const show = useMemo(() => hover || visible, [hover, visible]);

  if (disabled) return children;

  return (
    <Popover className={bem`tooltip ${className}`} {...other}>
      <Popover.Button ref={setRef} as="div" className={tooltipAnchorClassName} {...hoverProps}>
        {children}
      </Popover.Button>
      {show && content && (
        <Portal id="socialive-ui-atoms-tooltip">
          <Transition show={show}>
            <Popover.Panel
              static
              ref={setPopperElement}
              style={{ ...styles.popper, zIndex: 10000 }}
              {...attributes.popper}
              role="tooltip"
              className="tooltip__balloon"
            >
              {content}
              {showArrow && <div ref={setArrowElement} style={styles.arrow} className="tooltip__arrow" />}
            </Popover.Panel>
          </Transition>
        </Portal>
      )}
    </Popover>
  );
}

Tooltip.propTypes = {
  showArrow: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  tooltipAnchorClassName: PropTypes.string,
  children: PropTypes.node,
  content: PropTypes.node,
  placement: PropTypes.oneOf([
    'auto',
    'auto-start',
    'auto-end',
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'right',
    'right-start',
    'right-end',
    'left',
    'left-start',
    'left-end',
  ]),
  strategy: PropTypes.oneOf(['absolute', 'fixed']),
  xOffset: PropTypes.number,
  yOffset: PropTypes.number,
  visible: PropTypes.bool,
};

Tooltip.defaultProps = {
  showArrow: true,
  disabled: false,
  className: undefined,
  tooltipAnchorClassName: undefined,
  children: undefined,
  content: undefined,
  placement: 'top',
  strategy: 'absolute',
  xOffset: 0,
  yOffset: 8,
  visible: undefined,
};

export default Tooltip;
